import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Briefcase, ClipboardList, CalendarCheck, FileSignature, Lock,
  Clock, CheckCircle2, TrendingUp, FileText, AlertTriangle
} from "lucide-react";

// "My Space" dashboard for regular employees. Deliberately excludes:
//   • Company-wide job pipeline / SD activity
//   • Revenue, invoices, expenses, client billing
//   • Anyone else's daily entries or attendance
// Shows only the employee's own numbers so confidential org data stays hidden.
export default function EmployeeDashboard() {
  const { user } = useAuth();
  const [mine, setMine] = useState({
    active_jobs: 0, month_hours_given: 0, month_hours_taken: 0, month_efficiency: 0,
    month_entries: 0, month_attendance_days: 0, month_absent_days: 0,
  });
  const [docs, setDocs] = useState([]);
  const [retention, setRetention] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [statsRes, docsRes, retentionRes] = await Promise.all([
          api.get("/jobs/assignee-stats").catch(() => ({ data: { assignees: [] } })),
          api.get("/hr/me/documents").catch(() => ({ data: [] })),
          api.get("/hr/me/retention-status").catch(() => ({ data: null })),
        ]);
        const me = (statsRes.data.assignees || []).find((p) => p.name === user?.name);
        if (me) {
          setMine((s) => ({
            ...s,
            active_jobs: me.active_jobs || 0,
            month_hours_given: me.month_given_hours || 0,
            month_hours_taken: me.month_taken_hours || 0,
            month_efficiency: me.month_efficiency || 0,
            month_entries: me.month_entries || 0,
          }));
        }
        setDocs(docsRes.data || []);
        setRetention(retentionRes.data);
      } catch {
        /* ignore */
      }
      setLoading(false);
    };
    load();
  }, [user?.name]);

  const pendingDocs = docs.filter((d) => d.status === "pending_employee");
  const completedDocs = docs.filter((d) => d.status === "completed");
  const monthName = new Date().toLocaleDateString(undefined, { month: "long" });

  return (
    <div className="space-y-4" data-testid="employee-dashboard">
      <div>
        <div className="label-mono">MY SPACE</div>
        <h1 className="font-heading text-3xl font-black">Welcome, {user?.name?.split(" ")[0]}</h1>
        <p className="text-xs font-mono text-zinc-600">Your personal view — activities, tasks and HR documents assigned to you.</p>
      </div>

      {/* Retention banner */}
      {retention?.locked && (
        <div className="brut-card bg-rose-50 border-rose-700 p-3 flex items-center gap-2" data-testid="my-retention-banner">
          <Lock className="w-4 h-4 text-rose-700" />
          <div className="text-xs font-mono text-rose-900">
            <b>Retention lock active.</b> You are locked in until <b>{new Date(retention.locked_until).toLocaleDateString()}</b> ({retention.days_left} days left) — per your <code>{retention.source_template}</code>.
            {retention.extended_notice_days > 0 && <> A <b>{retention.extended_notice_days}-day</b> notice period will apply after that.</>}
          </div>
        </div>
      )}

      {/* Pending HR action banner */}
      {pendingDocs.length > 0 && (
        <Link to="/documentation" className="brut-card bg-amber-50 border-amber-700 p-3 flex items-center gap-2 hover:bg-amber-100" data-testid="my-pending-docs-banner">
          <AlertTriangle className="w-4 h-4 text-amber-700" />
          <div className="text-xs font-mono text-amber-900 flex-1">
            <b>{pendingDocs.length}</b> HR document{pendingDocs.length === 1 ? "" : "s"} awaiting your signature. Click to review.
          </div>
        </Link>
      )}

      {/* Top KPI tiles — self only */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile label="ACTIVE JOBS" value={mine.active_jobs} icon={Briefcase} accent="bg-yellow-200" testid="my-active-jobs" />
        <Tile label={`${monthName.toUpperCase()} TASKS`} value={mine.month_entries} icon={ClipboardList} accent="bg-emerald-200" testid="my-month-tasks" />
        <Tile label={`${monthName.toUpperCase()} HOURS`} value={`${mine.month_hours_taken}h / ${mine.month_hours_given}h`} icon={Clock} accent="bg-sky-200" small testid="my-month-hours" />
        <Tile label="EFFICIENCY" value={`${mine.month_efficiency}%`} icon={TrendingUp} accent={mine.month_efficiency >= 100 ? "bg-emerald-200" : mine.month_efficiency >= 70 ? "bg-yellow-200" : "bg-rose-200"} testid="my-efficiency" />
      </div>

      {/* Quick links */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <QuickCard to="/jobs" title="My Jobs" desc="See your assigned tasks and update stages." icon={Briefcase} testid="quick-jobs" />
        <QuickCard to="/daily-entries" title="Daily Entries" desc="Log today's task hours and delivered items." icon={ClipboardList} testid="quick-daily" />
        <QuickCard to="/attendance" title="My Attendance" desc="Check in / out and view your monthly attendance." icon={CalendarCheck} testid="quick-attendance" />
        <QuickCard to="/documentation" title="HR Documentation" desc={`${completedDocs.length} signed · ${pendingDocs.length} pending`} icon={FileSignature} testid="quick-docs" highlight={pendingDocs.length > 0} />
      </div>

      {/* Recent HR docs */}
      <div className="brut-card p-4">
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="label-mono">MY HR DOCUMENTS</div>
            <div className="font-heading text-lg font-bold">Recent</div>
          </div>
          <Link to="/documentation" className="text-xs font-mono underline">View all →</Link>
        </div>
        {loading ? (
          <div className="text-xs font-mono text-zinc-500">Loading…</div>
        ) : docs.length === 0 ? (
          <div className="text-xs font-mono text-zinc-500 py-3 text-center">No HR documents issued yet.</div>
        ) : (
          <ul className="divide-y divide-zinc-200" data-testid="my-docs-list">
            {docs.slice(0, 5).map((d) => (
              <li key={d.id} className="py-2 flex items-center justify-between text-sm">
                <span className="font-mono flex items-center gap-2 min-w-0">
                  <FileText className="w-3 h-3 shrink-0" />
                  <span className="truncate">{(d.template_type || "").replace(/_/g, " ")}</span>
                </span>
                <span className={`text-[10px] font-mono font-bold ${d.status === "completed" ? "text-emerald-700" : d.status === "pending_employee" ? "text-amber-700" : "text-zinc-500"}`}>
                  {d.status === "completed" ? <CheckCircle2 className="w-3 h-3 inline mr-1" /> : null}
                  {d.status.replace(/_/g, " ").toUpperCase()}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function Tile({ label, value, icon: Icon, accent, testid, small }) {
  return (
    <div className="brut-card p-3" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <div className="label-mono">{label}</div>
          <div className={`font-heading font-black mt-0.5 ${small ? "text-lg" : "text-3xl"}`}>{value}</div>
        </div>
        <div className={`w-8 h-8 border border-zinc-900 flex items-center justify-center shrink-0 ${accent}`}>
          <Icon className="w-4 h-4" strokeWidth={2.25} />
        </div>
      </div>
    </div>
  );
}

function QuickCard({ to, title, desc, icon: Icon, testid, highlight }) {
  return (
    <Link to={to} className={`brut-card p-3 hover:shadow-brut ${highlight ? "bg-amber-50 border-amber-700" : "bg-white"}`} data-testid={testid}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-4 h-4" />
        <div className="font-bold text-sm">{title}</div>
      </div>
      <div className="text-[10px] font-mono text-zinc-600">{desc}</div>
    </Link>
  );
}
